import useGameStore from "../context/GameContext";
import {
  WordSelectionLibrary,
  WordSelectionItem,
  FillInBlankLibraries,
  FillInBlankLibrariesSchema,
  WordSelectionLibraries,
  WordSelectionLibrariesSchema,
} from "../types/game";

/**
 * 加载词库
 * @param path 词库文件路径
 * @returns 处理后的两种类型的词库数据
 */
export const loadWordLibrary = async (
  path: string
): Promise<{
  selectionLibraries: WordSelectionLibraries;
  fillInBlankLibraries: FillInBlankLibraries;
}> => {
  try {
    console.log(`开始加载词库: ${path}`);
    const response = await fetch(path);

    if (!response.ok) {
      throw new Error(
        `加载词库失败: ${response.status} ${response.statusText}`
      );
    }

    const rawData = await response.json();

    // 分别存储两种类型的词库
    const selectionLibraries: Record<string, any[]> = {};
    const fillInBlankLibraries: Record<string, any[]> = {};

    // 处理每个题库，判断类型并分别存储
    for (const [libraryName, wordItems] of Object.entries(rawData)) {
      if (Array.isArray(wordItems) && wordItems.length > 0) {
        const firstItem = wordItems[0];

        // 判断词库类型
        if ("options" in firstItem) {
          // 这是选择题词库
          selectionLibraries[libraryName] = wordItems.map((item) => ({
            ...item,
            type: "selection",
          }));
        } else if ("sentence" in firstItem) {
          // 这是填空题词库
          fillInBlankLibraries[libraryName] = wordItems.map((item) => ({
            ...item,
            type: "fillInBlank",
          }));
        } else {
          console.warn(`未知词库类型: ${libraryName}`);
        }
      } else {
        // 空词库，暂时忽略
        console.warn(`空词库: ${libraryName}`);
      }
    }

    // 使用Zod验证两种类型的词库
    const selectionResult =
      WordSelectionLibrariesSchema.safeParse(selectionLibraries);
    const fillInBlankResult =
      FillInBlankLibrariesSchema.safeParse(fillInBlankLibraries);

    // 验证选择题词库
    if (!selectionResult.success) {
      console.error("选择题词库验证错误:", selectionResult.error.format());
    }

    // 验证填空题词库
    if (!fillInBlankResult.success) {
      console.error("填空题词库验证错误:", fillInBlankResult.error.format());
    }

    // 即使有验证错误，也使用处理后的数据继续
    const validatedSelectionLibraries = selectionResult.success
      ? selectionResult.data
      : (selectionLibraries as WordSelectionLibraries);

    const validatedFillInBlankLibraries = fillInBlankResult.success
      ? fillInBlankResult.data
      : (fillInBlankLibraries as FillInBlankLibraries);

    // 更新GameStore
    const gameStore = useGameStore.getState();
    gameStore.setSelectionLibraries(validatedSelectionLibraries);
    gameStore.setFillInBlankLibraries(validatedFillInBlankLibraries);

    // 统计词库信息
    const selectionLibraryCount = Object.keys(
      validatedSelectionLibraries
    ).length;
    const fillInBlankLibraryCount = Object.keys(
      validatedFillInBlankLibraries
    ).length;

    console.log(`词库加载成功: ${path}`);
    console.log(`- 选择题词库: ${selectionLibraryCount} 个`);
    console.log(`- 填空题词库: ${fillInBlankLibraryCount} 个`);

    return {
      selectionLibraries: validatedSelectionLibraries,
      fillInBlankLibraries: validatedFillInBlankLibraries,
    };
  } catch (error) {
    console.error("加载词库出错:", error);
    // 返回空词库，避免程序崩溃
    return {
      selectionLibraries: {},
      fillInBlankLibraries: {},
    };
  }
};

/**
 * 根据当前域名自动选择词库
 * @returns 对应域名的词库路径
 */
export const getLibraryPathByDomain = (): string => {
  const hostname = window.location.hostname;

  // 根据域名返回不同的词库路径
  if (hostname === "words.kezhow.cn") {
    return "/assets/words-libraries/question-library.json";
  } else if (hostname.includes("localhost")) {
    // 本地开发环境
    return "/assets/words-libraries/dev-library.json";
  } else {
    // 默认词库
    return "/assets/words-libraries/xhs-library.json";
  }
};

/**
 * 获取词库中的所有题集名称
 * @param library 词库
 * @returns 题集名称数组
 */
export const getLibraryNames = (library: WordSelectionLibrary): string[] => {
  return Object.keys(library);
};

/**
 * 获取特定题集中的单词数量
 * @param library 词库
 * @param name 题集名称
 * @returns 单词数量
 */
// export const getWordCount = (
//   library: WordSelectionLibrary,
//   name: string
// ): number => {
//   return library[name]?.length || 0;
// };

/**
 * 从词库中获取特定题集的单词
 * @param library 词库
 * @param name 题集名称
 * @returns 单词数组
 */
// export const getWordsFromLibrary = (
//   library: WordSelectionLibrary,
//   name: string
// ): WordSelectionItem[] => {
//   return library[name] || [];
// };

/**
 * 将本地JSON文件转换为词库格式
 * 可用于导入自定义词库文件
 * @param file 本地JSON文件
 * @returns Promise 解析后的词库
 */
export const importLibraryFromFile = (
  file: File
): Promise<WordSelectionLibrary> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        if (event.target?.result) {
          const data = JSON.parse(event.target.result as string);
          validateWordLibrary(data);
          resolve(data);
        } else {
          reject(new Error("读取文件失败"));
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("读取文件出错"));
    };

    reader.readAsText(file);
  });
};

/**
 * 导出词库为JSON文件
 * @param library 词库对象
 * @param filename 文件名
 */
export const exportLibraryToFile = (
  library: WordSelectionLibrary,
  filename: string = "word-library.json"
): void => {
  const json = JSON.stringify(library, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  // 释放URL对象
  setTimeout(() => URL.revokeObjectURL(url), 100);
};
function detectItemType(item: any) {
  throw new Error("Function not implemented.");
}

function validateWordLibrary(data: any) {
  throw new Error("Function not implemented.");
}
